package com.nonopn.skywatcher.common

import com.nonopn.skywatcher.app.utils.Queue
import com.nonopn.skywatcher.app.utils.RootPath
import com.nonopn.skywatcher.app.utils.readStringIfExists
import com.nonopn.skywatcher.common.api.APIUserSession
import com.nonopn.skywatcher.common.api.ApiNoSession
import com.nonopn.skywatcher.common.api.ApiProvider
import com.nonopn.skywatcher.common.api.ApiSession
import com.nonopn.skywatcher.common.api.LoginRequest
import com.nonopn.skywatcher.common.api.SavedSession
import eu.codlab.files.VirtualFile
import eu.codlab.http.Configuration
import eu.codlab.http.createClient
import io.ktor.client.HttpClient
import io.ktor.client.plugins.auth.providers.BearerTokens
import io.ktor.client.plugins.auth.providers.bearer
import io.ktor.utils.io.core.toByteArray
import korlibs.io.async.CIO
import korlibs.time.DateTime
import korlibs.time.milliseconds
import korlibs.time.months
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.serialization.KSerializer
import kotlinx.serialization.json.Json

private var baseUrl = "https://api.lpsmanager.io"
const val Millis = 1000L

class SessionManager<T, D>(
    folder: String,
    private val serializer: KSerializer<D>,
    val admin: (HttpClient) -> T,
    val normal: (HttpClient) -> T
) {
    private val queue = Queue(CoroutineScope(Dispatchers.CIO))
    private val RootDir = VirtualFile(RootPath, "NonOpn/$folder")
    private val sessionFile = VirtualFile(RootDir, "user.json")
    private var savedSession: SavedSession<D>? = null

    fun getFile(fileName: String) = VirtualFile(RootDir, fileName)

    private val apiNoSession = ApiNoSession(baseUrl, createClient { })

    suspend fun getSpecificData() = readSessionFile()?.specificData

    private suspend fun readSessionFile(): SavedSession<D>? = try {
        if (null == savedSession) {
            savedSession = sessionFile.readStringIfExists()?.let {
                Json.decodeFromString(SavedSession.serializer(serializer), it)
            }
        }

        savedSession
    } catch (err: Throwable) {
        err.printStackTrace()
        null
    }

    suspend fun initialize(): ApiProvider<T>? {
        if (!RootDir.exists()) {
            RootDir.mkdirs()
        }

        val session = readSessionFile()

        return try {
            val apiProvider = generateClient(session?.token, false)

            val userFromBackend = apiProvider!!.apiSession.profile()

            saveTokenFromBackend(userFromBackend.persistenceToken, 60 * 60 * 24 * 30 * 6)

            generateClient(userFromBackend.persistenceToken, userFromBackend.admin)
        } catch (_: Throwable) {
            null
        }
    }

    private suspend fun saveTokenFromBackend(token: String? = null, expiresIn: Long) {
        val now = DateTime.now().add(0.months, (expiresIn * Millis).milliseconds)
        val specificData = readSessionFile()?.specificData
        SavedSession(
            token = token,
            expiresAt = now.unixMillisLong,
            specificData = specificData
        ).let {
            savedSession = it
            Json.encodeToString(
                SavedSession.serializer(
                    serializer
                ),
                it
            )
        }.let {
            queue.enqueue {
                sessionFile.write(it.toByteArray())
            }
        }
    }

    suspend fun saveSpecificData(specificData: D) {
        (readSessionFile() ?: SavedSession(expiresAt = 0L))
            .copy(specificData = specificData)
            .let {
                savedSession = it

                queue.enqueue {
                    sessionFile.write(
                        Json.encodeToString(
                            SavedSession.serializer(
                                serializer
                            ),
                            it
                        ).toByteArray()
                    )
                }
            }
    }

    suspend fun register(email: String, password: String) =
        callRegister { apiNoSession.register(LoginRequest(email, password)) }

    suspend fun login(email: String, password: String) =
        callRegister { apiNoSession.login(LoginRequest(email, password)) }

    private suspend fun callRegister(call: suspend () -> APIUserSession): ApiProvider<T> {
        val session = call()

        saveTokenFromBackend(session.persistenceToken, 60 * 60 * 24 * 30 * 6) //6 months

        if (!sessionFile.exists()) {
            throw IllegalStateException(
                "unable to find the file for the session " +
                        sessionFile.absolutePath
            )
        }

        return generateClient(session.persistenceToken, session.admin ?: false)!!
    }

    private fun generateClient(
        token: String? = null,
        generateWithAdmin: Boolean?
    ): ApiProvider<T>? {
        if (null == token) {
            return null
        }

        val wrapper = createClient(
            configuration = Configuration(
                enableSocket = true,
                enableLogs = false,
                connectTimeoutMillis = 30000,
                requestTimeoutMillis = 30000,
                socketTimeoutMillis = 30000
            ),
            onAuth = {
                bearer {
                    loadTokens {
                        BearerTokens(token, "")
                    }
                }
            }
        )

        return ApiProvider(
            apiAdmin = if (generateWithAdmin == true) {
                admin(wrapper)
            } else {
                null
            },
            apiNormal = normal(wrapper),
            apiSession = ApiSession(wrapper)
        )
    }

    suspend fun logout() = saveTokenFromBackend(null, 0)
}
